import type { ReactElement, SVGProps } from 'react';
import { DeFlag } from './de';
import { DkFlag } from './dk';
import { FiFlag } from './fi';
import { FrFlag } from './fr';
import { GbFlag } from './gb';
import { IeFlag } from './ie';
import { ItFlag } from './it';
import { JpFlag } from './jp';
import { NlFlag } from './nl';
import { NoFlag } from './no';
import { PlFlag } from './pl';
import { SeFlag } from './se';

export type FlagComponent = (props: SVGProps<SVGSVGElement>) => ReactElement;

/**
 * Region (ISO 3166-1 alpha-2) → flag component. Regions without a drawn flag (MX, KR, BR, CN, TW) have no
 * entry and resolve to `undefined` so the caller falls back (e.g. currency code only). Add + register to
 * light one up — no caller change needed.
 */
const FLAG_BY_REGION: Record<string, FlagComponent> = {
    DE: DeFlag,
    DK: DkFlag,
    FI: FiFlag,
    FR: FrFlag,
    GB: GbFlag,
    IE: IeFlag,
    IT: ItFlag,
    JP: JpFlag,
    NL: NlFlag,
    NO: NoFlag,
    PL: PlFlag,
    SE: SeFlag,
};

/**
 * Uppercase ISO 3166-1 alpha-2 region for a BCP-47 locale id. Uses the id's own region, else infers the
 * likely one from the language via CLDR likely-subtags (`Intl.Locale.maximize`) — so `"nl"` → `"NL"`,
 * `"ja"` → `"JP"`. Returns `undefined` when no 2-letter country resolves (unknown language, or an M49
 * macro-region like `"es-419"`).
 */
export function localeIdToRegion(localeId: string | undefined): string | undefined {
    if (!localeId) {
        return undefined;
    }
    try {
        const region = new Intl.Locale(localeId).maximize().region;
        return region && /^[A-Z]{2}$/.test(region) ? region : undefined;
    } catch {
        // Malformed locale id — Intl.Locale throws RangeError.
        return undefined;
    }
}

/** Flag component for a locale id, or `undefined` when the region is unknown or has no flag drawn yet. */
export function getFlagComponent(localeId: string | undefined): FlagComponent | undefined {
    const region = localeIdToRegion(localeId);
    return region ? FLAG_BY_REGION[region] : undefined;
}
